// lib/errors.ts
// Centralized error handling — AppError class for typed, structured errors
// across both server and client code. All errors carry a code, HTTP status,
// and optional context for debugging without leaking internals to users.
//
// Usage:
//   throw AppError.notFound('Project', 'abc-123')
//   throw AppError.validation('Email is required', 'email')
//   throw AppError.bridgeError('Bridge returned 502')
//
// Route middleware wrappers catch AppError and convert it to a standardized
// JSON response via errorResponse(). No raw try/catch needed in handlers.

/** Exhaustive enum of all error codes used across SiteGenius. */
export const ErrorCode = {
  // --- Client errors (4xx) ---
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  BAD_REQUEST: 'BAD_REQUEST',
  AUTH_REQUIRED: 'AUTH_REQUIRED',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  CSRF_INVALID: 'CSRF_INVALID',
  NOT_FOUND: 'NOT_FOUND',
  CONFLICT: 'CONFLICT',
  RATE_LIMITED: 'RATE_LIMITED',
  APPROVAL_REQUIRED: 'APPROVAL_REQUIRED',
  PAYLOAD_TOO_LARGE: 'PAYLOAD_TOO_LARGE',
  DEPLOY_CONFLICT: 'DEPLOY_CONFLICT',

  // --- Server errors (5xx) ---
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
  BRIDGE_ERROR: 'BRIDGE_ERROR',
  GENERATION_FAILED: 'GENERATION_FAILED',
} as const

export type ErrorCode = (typeof ErrorCode)[keyof typeof ErrorCode]

/** Default human-readable messages for each error code. */
const defaultMessages: Record<ErrorCode, string> = {
  [ErrorCode.VALIDATION_ERROR]: 'Validation failed',
  [ErrorCode.BAD_REQUEST]: 'Bad request',
  [ErrorCode.AUTH_REQUIRED]: 'Authentication required',
  [ErrorCode.UNAUTHORIZED]: 'Authentication required',
  [ErrorCode.FORBIDDEN]: 'Access denied',
  [ErrorCode.CSRF_INVALID]: 'Invalid CSRF token',
  [ErrorCode.NOT_FOUND]: 'Resource not found',
  [ErrorCode.CONFLICT]: 'Resource conflict',
  [ErrorCode.RATE_LIMITED]: 'Rate limited. Please try again later.',
  [ErrorCode.APPROVAL_REQUIRED]: 'Account pending admin approval',
  [ErrorCode.PAYLOAD_TOO_LARGE]: 'Request payload too large',
  [ErrorCode.DEPLOY_CONFLICT]: 'Deploy already in progress',
  [ErrorCode.INTERNAL_ERROR]: 'Internal server error',
  [ErrorCode.SERVICE_UNAVAILABLE]: 'Service temporarily unavailable',
  [ErrorCode.BRIDGE_ERROR]: 'Bridge service error',
  [ErrorCode.GENERATION_FAILED]: 'Site generation failed',
}

/** Optional structured context attached to errors for debugging. */
export interface ErrorContext {
  /** The field or parameter that caused the error */
  field?: string
  /** Additional details for debugging (never sent to client in production) */
  detail?: string
  /** Retry-After header value in seconds (for rate limiting) */
  retryAfterSeconds?: number
  /** Original error, if wrapping */
  cause?: unknown
}

/**
 * Typed application error with code, HTTP status, and optional context.
 *
 * All API route errors should be instances of AppError. The middleware
 * wrappers catch AppError and convert it to a standardized JSON response.
 * Unknown errors are wrapped via wrapError() or toAppError().
 */
export class AppError extends Error {
  public readonly code: ErrorCode
  public readonly statusCode: number
  public readonly context?: ErrorContext
  public readonly isOperational: boolean

  constructor(
    message: string,
    code: ErrorCode,
    statusCode: number,
    context?: ErrorContext,
  ) {
    super(message)
    this.name = 'AppError'
    this.code = code
    this.statusCode = statusCode
    this.context = context
    // Operational errors are expected (validation, not found, etc.)
    // Non-operational errors are bugs that should trigger alerts
    this.isOperational = statusCode < 500

    // Ensure proper prototype chain for instanceof checks
    Object.setPrototypeOf(this, AppError.prototype)
  }

  /** Serialize for API responses — strips internal context in production. */
  toJSON(): {
    error: {
      code: ErrorCode
      message: string
      context?: Record<string, unknown>
    }
  } {
    const base: {
      error: {
        code: ErrorCode
        message: string
        context?: Record<string, unknown>
      }
    } = {
      error: {
        code: this.code,
        message: this.message,
      },
    }

    if (import.meta.env.MODE !== 'production' && this.context) {
      base.error.context = {
        field: this.context.field,
        detail: this.context.detail,
      }
    }

    return base
  }

  // ---------------------------------------------------------------
  // Factory methods — one for each common error scenario
  // ---------------------------------------------------------------

  /** 400 Bad Request */
  static badRequest(message: string, context?: ErrorContext): AppError {
    return new AppError(message, ErrorCode.BAD_REQUEST, 400, context)
  }

  /** 401 Unauthorized — missing or invalid credentials */
  static unauthorized(
    message = defaultMessages[ErrorCode.UNAUTHORIZED],
  ): AppError {
    return new AppError(message, ErrorCode.UNAUTHORIZED, 401)
  }

  /** 401 Auth Required — alias that uses AUTH_REQUIRED code */
  static authRequired(
    message = defaultMessages[ErrorCode.AUTH_REQUIRED],
  ): AppError {
    return new AppError(message, ErrorCode.AUTH_REQUIRED, 401)
  }

  /** 403 Forbidden — authenticated but not authorized */
  static forbidden(message = defaultMessages[ErrorCode.FORBIDDEN]): AppError {
    return new AppError(message, ErrorCode.FORBIDDEN, 403)
  }

  /** 403 CSRF Invalid — CSRF token missing or mismatched */
  static csrfInvalid(
    message = defaultMessages[ErrorCode.CSRF_INVALID],
  ): AppError {
    return new AppError(message, ErrorCode.CSRF_INVALID, 403)
  }

  /** 404 Not Found — resource does not exist */
  static notFound(resource: string, id?: string): AppError {
    const message = id
      ? `${resource} with id "${id}" not found`
      : `${resource} not found`
    return new AppError(message, ErrorCode.NOT_FOUND, 404)
  }

  /** 409 Conflict — resource already exists or version mismatch */
  static conflict(message: string, context?: ErrorContext): AppError {
    return new AppError(message, ErrorCode.CONFLICT, 409, context)
  }

  /** 413 Payload Too Large */
  static payloadTooLarge(
    message = defaultMessages[ErrorCode.PAYLOAD_TOO_LARGE],
  ): AppError {
    return new AppError(message, ErrorCode.PAYLOAD_TOO_LARGE, 413)
  }

  /** 422 Validation Error — invalid input data */
  static validation(message: string, field?: string): AppError {
    return new AppError(message, ErrorCode.VALIDATION_ERROR, 422, { field })
  }

  /** 403 Approval Required — user account not yet approved by admin */
  static approvalRequired(
    message = defaultMessages[ErrorCode.APPROVAL_REQUIRED],
  ): AppError {
    return new AppError(message, ErrorCode.APPROVAL_REQUIRED, 403)
  }

  /** 429 Rate Limited — too many requests */
  static rateLimited(retryAfterSeconds?: number): AppError {
    const message = retryAfterSeconds
      ? `Rate limited. Retry after ${retryAfterSeconds} seconds.`
      : defaultMessages[ErrorCode.RATE_LIMITED]
    return new AppError(message, ErrorCode.RATE_LIMITED, 429, {
      retryAfterSeconds,
    })
  }

  /** 500 Internal Server Error — unexpected failure */
  static internal(
    message = defaultMessages[ErrorCode.INTERNAL_ERROR],
    cause?: unknown,
  ): AppError {
    return new AppError(message, ErrorCode.INTERNAL_ERROR, 500, { cause })
  }

  /** 502 Bridge Error — bridge service returned an error */
  static bridgeError(
    message = defaultMessages[ErrorCode.BRIDGE_ERROR],
    cause?: unknown,
  ): AppError {
    return new AppError(message, ErrorCode.BRIDGE_ERROR, 502, { cause })
  }

  /** 500 Generation Failed — site generation pipeline error */
  static generationFailed(
    message = defaultMessages[ErrorCode.GENERATION_FAILED],
    cause?: unknown,
  ): AppError {
    return new AppError(message, ErrorCode.GENERATION_FAILED, 500, { cause })
  }

  /** 503 Service Unavailable — downstream service is down */
  static serviceUnavailable(
    message = defaultMessages[ErrorCode.SERVICE_UNAVAILABLE],
  ): AppError {
    return new AppError(message, ErrorCode.SERVICE_UNAVAILABLE, 503)
  }
}

/**
 * Type guard to check if an unknown value is an AppError instance.
 *
 * @example
 *   try { ... } catch (err) {
 *     if (isAppError(err)) console.log(err.code)
 *   }
 */
export function isAppError(error: unknown): error is AppError {
  return error instanceof AppError
}

/**
 * Wraps an unknown error into an AppError. If it's already an AppError,
 * returns it as-is. Otherwise wraps it as an internal error preserving
 * the original message when available.
 *
 * Use this at error boundaries (middleware wrappers, catch blocks) to
 * ensure every error flowing through the system is a typed AppError.
 */
export function wrapError(error: unknown): AppError {
  if (isAppError(error)) return error

  if (error instanceof Error) {
    return AppError.internal(error.message, error)
  }

  if (typeof error === 'string') {
    return AppError.internal(error)
  }

  return AppError.internal('An unexpected error occurred', error)
}

/**
 * Alias for wrapError — maintains backward compatibility with the
 * original skeleton's toAppError naming.
 */
export const toAppError = wrapError
